import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBandeau } from '../BandeauContext';
import { apiClient } from '../../utils/apiClient';
import ActionBtn from './ActionBtn';
import TabBar from '../TabBar';
import Modal from '../common/Modal';
import { BookOpen, Image as ImageIcon, Loader2, Clapperboard } from 'lucide-react';
import { findBySlug, buildCoursePath, generateSlug } from '../../utils/slugUtils';
import { SubjectIcon } from '../SubjectIcons';
import MarkdownEditor from './MarkdownEditor';
import NotionPreview from './NotionPreview';
import { renderContent } from '../renderContent';
import FlashcardSession from '../student/FlashcardSession';
import QuizSession from '../student/QuizSession';
import NotionStatsModal, { calcDifficultyRatio } from './NotionStatsModal';
import {
    DndContext, PointerSensor, useSensor, useSensors,
    closestCenter, DragOverlay, useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext, useSortable,
    verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BASE = process.env.REACT_APP_API_BASE_URL;

const SCHOOL_YEARS = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];

function computeLevelSchoolYear(startYear, levelNumber) {
    const idx = SCHOOL_YEARS.indexOf(startYear);
    if (idx === -1) return startYear;
    return SCHOOL_YEARS[idx + (levelNumber - 1)] ?? startYear;
}

// Miroir JS de parseDraftBlocks (PHP) — convertit le markdown :::type en blocs DB
function parseDraftBlocks(text) {
    const VALID = new Set(['definition', 'theorem', 'proof', 'example', 'remark', 'note', 'image_prompt']);
    const blocks = [];
    let order = 0;

    function splitPlain(raw) {
        const segs = [];
        for (const para of raw.trim().split(/\n{2,}/)) {
            const p = para.trim();
            if (!p) continue;
            segs.push({ type: /^#{1,6}\s/.test(p) ? 'heading' : 'paragraph', title: null, content: p });
        }
        return segs;
    }

    const re = /^:::(\w+)(?:[^\S\n]+(.+?))?\n([\s\S]*?)^:::/gm;
    let lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        for (const seg of splitPlain(text.slice(lastIndex, m.index)))
            blocks.push({ ...seg, id: `d${order}`, display_order: order++ });
        const type = m[1].toLowerCase();
        blocks.push({
            id: `d${order}`, display_order: order++,
            type: VALID.has(type) ? type : 'paragraph',
            title: m[2]?.trim() || null,
            content: m[3].trim(),
        });
        lastIndex = m.index + m[0].length;
    }
    for (const seg of splitPlain(text.slice(lastIndex)))
        blocks.push({ ...seg, id: `d${order}`, display_order: order++ });

    return blocks;
}

const EMPTY_SUBJECT = { id: null, name: '', display_order: 0, weight_notion: 60, weight_exercice: 40, mastery_threshold: 80, new_items_per_day: 10 };
const EMPTY_CHAPTER = { id: null, subject_id: '', name: '', description: '', display_group: '', school_year: '', display_order: 0 };
const EMPTY_NOTION = { id: null, chapter_id: '', number: '', name: '', description: '', min_score: 80, display_order: 0 };
const EMPTY_COURSE = { id: null, notion_id: '', title: '', content: '', type: 'paragraph', display_order: 0 };

const QUIZ_TYPES = [
    { value: 'single_choice', label: 'Choix unique' },
    { value: 'multiple_choice', label: 'Choix multiple' },
    { value: 'matching', label: 'Association' },
    { value: 'short_text', label: 'Texte court' },
    { value: 'numeric', label: 'Numérique' },
    { value: 'fill_in_the_blank', label: 'Texte à trous' },
    { value: 'ordering', label: 'Mise en ordre' },
];

const QUIZ_TYPE_COLORS = {
    single_choice: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    multiple_choice: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    matching: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    short_text: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    numeric: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    fill_in_the_blank: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    ordering: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

const QUIZ_TYPE_LABELS = Object.fromEntries(QUIZ_TYPES.map(t => [t.value, t.label]));

const BLOCK_TYPES = [
    { value: 'paragraph', label: 'Paragraphe' },
    { value: 'heading', label: 'Titre (##)' },
    { value: 'definition', label: 'Définition' },
    { value: 'theorem', label: 'Théorème' },
    { value: 'proof', label: 'Démonstration' },
    { value: 'example', label: 'Exemple' },
    { value: 'remark', label: 'Remarque' },
    { value: 'note', label: 'Note' },
    { value: 'image_prompt', label: 'Schéma à générer' },
];

const TYPE_COLORS = {
    definition: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    theorem: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    proof: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400',
    example: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    remark: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    note: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    image_prompt: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    heading: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300',
    paragraph: 'bg-gray-50 text-gray-500 dark:bg-white/5 dark:text-gray-500',
    text: 'bg-gray-50 text-gray-500 dark:bg-white/5 dark:text-gray-500',
};

const TYPE_LABELS = Object.fromEntries(BLOCK_TYPES.map(t => [t.value, t.label]));
TYPE_LABELS.text = 'Paragraphe';


function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</label>
            {children}
        </div>
    );
}

function Input({ value, onChange, placeholder, type = 'text' }) {
    return (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
    );
}

function Select({ value, onChange, options, placeholder }) {
    return (
        <select value={value} onChange={e => onChange(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500">
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}



function DroppableNotion({ notion, isSelected, onClick }) {
    const { setNodeRef, isOver } = useDroppable({ id: `notion-${notion.id}` });
    return (
        <button
            ref={setNodeRef}
            onClick={onClick}
            className={`w-full text-left ml-4 px-3 py-1 rounded-lg text-xs transition-colors
                ${isSelected ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-300' : 'text-gray-500 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5'}
                ${isOver ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : ''}`}
        >
            ◆ {notion.number} {notion.name}
        </button>
    );
}

function SortableBlock({ block, idx, isSelected, onToggleSelect, onEdit, onDelete, onGenerate, onPublish }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
    const [generating, setGenerating] = React.useState(null);
    const [pollStep, setPollStep] = React.useState(0);
    const typeColor = TYPE_COLORS[block.type] ?? TYPE_COLORS.paragraph;
    const typeLabel = TYPE_LABELS[block.type] ?? block.type;

    const handleGenerate = async (mediaType) => {
        setGenerating(mediaType);
        setPollStep(0);
        await onGenerate(block, mediaType, (step) => setPollStep(step));
        setGenerating(null);
        setPollStep(0);
    };

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
            className={`rounded-xl border overflow-hidden transition-colors ${isDragging ? 'opacity-40' : ''} ${isSelected ? 'border-blue-400 dark:border-blue-500 ring-1 ring-blue-400' : 'border-gray-200 dark:border-white/10'}`}
        >
            <div className={`flex items-center justify-between px-4 py-2.5 cursor-pointer select-none transition-colors ${block.status === 'draft' ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-800/60'}`} onClick={() => onToggleSelect(block.id)}>
                <div className="flex items-center gap-2 min-w-0">
                    <button {...attributes} {...listeners} onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-grab active:cursor-grabbing shrink-0 touch-none text-lg leading-none">⠿</button>
                    <span className="text-xs text-gray-400 font-mono shrink-0">{idx + 1}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${typeColor}`}>{typeLabel}</span>
                    {block.status === 'draft' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-200 dark:bg-amber-800/60 text-amber-700 dark:text-amber-300 shrink-0">Brouillon</span>
                    )}
                    {block.title && <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate [&_p]:inline [&_p]:m-0">{renderContent(block.title)}</span>}
                </div>
                <div className="flex gap-1.5 shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                    {block.type === 'image_prompt' && (
                        <>
                            <ActionBtn variant="add" onClick={() => handleGenerate('image')} disabled={!!generating}>
                                {generating === 'image' ? (
                                    <span className="flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Génération…</span></span>
                                ) : (
                                    <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /><span>Image</span></span>
                                )}
                            </ActionBtn>
                            <ActionBtn variant="add" onClick={() => handleGenerate('video')} disabled={!!generating}>
                                {generating === 'video' ? (
                                    <span className="flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{pollStep * 10}s…</span></span>
                                ) : (
                                    <span className="flex items-center gap-1"><Clapperboard className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /><span>Animation</span></span>
                                )}
                            </ActionBtn>
                        </>
                    )}
                    {block.status === 'draft'
                        ? <ActionBtn variant="publish" onClick={() => onPublish(block.id, 'publish')}>Publier</ActionBtn>
                        : <ActionBtn variant="draft" onClick={() => onPublish(block.id, 'unpublish')}>Dépublier</ActionBtn>
                    }
                    <ActionBtn onClick={() => onEdit(block)}>Modifier</ActionBtn>
                    <ActionBtn variant="danger" onClick={() => onDelete(block)}>✕</ActionBtn>
                </div>
            </div>
            <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 [&_p]:m-0 [&_p+p]:mt-2">
                {block.content ? renderContent(block.content) : <em className="text-gray-400 text-xs">Pas de contenu</em>}
                {block.type === 'image_prompt' && block.media_url && (
                    <div className="mt-3">
                        {block.media_type === 'image' && (
                            <img src={block.media_url} alt={block.media_description || ''} className="rounded-xl w-full max-w-sm shadow" />
                        )}
                        {block.media_type === 'video' && (
                            <video controls className="rounded-xl w-full max-w-sm shadow" src={block.media_url} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function PreviewModal({ onClose, children }) {
    return (
        <div className="fixed inset-0 z-[110] overflow-y-auto bg-black/60 p-4 flex items-start justify-center" onClick={onClose}>
            <div className="relative w-full max-w-2xl mt-8 mb-8 bg-white dark:bg-gray-950 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none bg-gray-100 dark:bg-gray-800 rounded-full w-8 h-8 flex items-center justify-center">✕</button>
                <div className="p-6 overflow-hidden">
                    {children}
                </div>
            </div>
        </div>
    );
}

// ─── Modal stats par flashcard ───────────────────────────────────
function FlashcardStatsModal({ fc, notionId, onClose }) {
    const [stat, setStat] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey); };
    }, [onClose]);

    useEffect(() => {
        setLoading(true);
        apiClient(`${BASE}/admin_stats.php?action=flashcard&notion_id=${notionId}`)
            .then(r => r.json())
            .then(d => {
                if (d.success) {
                    const found = d.data.find(f => String(f.id) === String(fc.id));
                    setStat(found ?? null);
                }
            })
            .finally(() => setLoading(false));
    }, [fc.id, notionId]);

    const total = stat?.total_attempts ?? 0;
    const hard = stat?.hard_count ?? 0;
    const medium = stat?.medium_count ?? 0;
    const easy = stat?.easy_count ?? 0;
    const rate = stat?.success_rate ?? null;
    const students = stat?.unique_students ?? 0;

    const pct = (n) => total > 0 ? Math.round(n / total * 100) : 0;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/10">
                    <div className="min-w-0">
                        <p className="text-[10px] font-semibold text-fuchsia-500 uppercase tracking-wider mb-1">Stats — flashcard</p>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 [&_p]:m-0 [&_p+p]:mt-1">{renderContent(fc.question)}</div>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">✕</button>
                </div>

                <div className="overflow-y-auto p-5 space-y-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
                            <div className="w-5 h-5 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm">Chargement…</span>
                        </div>
                    ) : total === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">Aucune tentative enregistrée.</p>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <div className="flex h-3 rounded-full overflow-hidden gap-px">
                                    <div className="bg-red-400" style={{ width: `${pct(hard)}%` }} />
                                    <div className="bg-orange-400" style={{ width: `${pct(medium)}%` }} />
                                    <div className="bg-emerald-400" style={{ width: `${pct(easy)}%` }} />
                                </div>
                                <div className="flex gap-4 text-[11px] text-gray-500">
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1" />Difficile {pct(hard)}%</span>
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1" />Moyen {pct(medium)}%</span>
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />Facile {pct(easy)}%</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{total}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">tentatives</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className={`text-xl font-bold ${rate === null ? 'text-gray-400' : rate < 40 ? 'text-red-500' : rate < 70 ? 'text-orange-400' : 'text-emerald-500'}`}>
                                        {rate !== null ? `${rate}%` : '—'}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">réussite</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{students}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">élèves</p>
                                </div>
                            </div>

                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Modal stats par quiz ────────────────────────────────────────
function QuizStatsModal({ quiz, notionId, onClose }) {
    const [stat, setStat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wrongAnswers, setWrongAnswers] = useState(null);
    const [loadingWrong, setLoadingWrong] = useState(true);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey); };
    }, [onClose]);

    useEffect(() => {
        setLoading(true);
        setLoadingWrong(true);
        Promise.all([
            apiClient(`${BASE}/admin_stats.php?action=quiz&notion_id=${notionId}`)
                .then(r => r.json()),
            apiClient(`${BASE}/admin_stats.php?action=quiz_detail&id=${quiz.id}`)
                .then(r => r.json()),
        ]).then(([qJson, dJson]) => {
            if (qJson.success) {
                const found = qJson.data.find(q => String(q.id) === String(quiz.id));
                setStat(found ?? null);
            }
            if (dJson.wrong_answers) setWrongAnswers(dJson.wrong_answers);
        }).finally(() => { setLoading(false); setLoadingWrong(false); });
    }, [quiz.id, notionId]);

    const total = stat?.total_attempts ?? 0;
    const hard = stat?.hard_count ?? 0;
    const medium = stat?.medium_count ?? 0;
    const easy = stat?.easy_count ?? 0;
    const rate = stat?.success_rate ?? null;
    const avgT = stat?.avg_time ?? null;

    const pct = (n) => total > 0 ? Math.round(n / total * 100) : 0;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/10">
                    <div className="min-w-0">
                        <p className="text-[10px] font-semibold text-fuchsia-500 uppercase tracking-wider mb-1">Stats — quiz</p>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 [&_p]:m-0 [&_p+p]:mt-1">{renderContent(quiz.question)}</div>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">✕</button>
                </div>

                <div className="overflow-y-auto p-5 space-y-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
                            <div className="w-5 h-5 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm">Chargement…</span>
                        </div>
                    ) : total === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">Aucune tentative enregistrée.</p>
                    ) : (
                        <>
                            {/* Barre de difficulté */}
                            <div className="space-y-2">
                                <div className="flex h-3 rounded-full overflow-hidden gap-px">
                                    <div className="bg-red-400" style={{ width: `${pct(hard)}%` }} title={`Difficile ${pct(hard)}%`} />
                                    <div className="bg-orange-400" style={{ width: `${pct(medium)}%` }} title={`Moyen ${pct(medium)}%`} />
                                    <div className="bg-emerald-400" style={{ width: `${pct(easy)}%` }} title={`Facile ${pct(easy)}%`} />
                                </div>
                                <div className="flex gap-4 text-[11px] text-gray-500">
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1" />Difficile {pct(hard)}%</span>
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1" />Moyen {pct(medium)}%</span>
                                    <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-1" />Facile {pct(easy)}%</span>
                                </div>
                            </div>

                            {/* Chiffres clés */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{total}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">tentatives</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className={`text-xl font-bold ${rate === null ? 'text-gray-400' : rate < 40 ? 'text-red-500' : rate < 70 ? 'text-orange-400' : 'text-emerald-500'}`}>
                                        {rate !== null ? `${rate}%` : '—'}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">réussite</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                                    <p className="text-xl font-bold text-gray-800 dark:text-gray-200">
                                        {avgT !== null ? `${avgT}s` : '—'}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">temps moy.</p>
                                </div>
                            </div>

                            {/* Mauvaises réponses */}
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Mauvaises réponses fréquentes</p>
                                {loadingWrong ? (
                                    <p className="text-xs text-gray-400">Chargement…</p>
                                ) : wrongAnswers?.length > 0 ? (
                                    <div className="space-y-1.5">
                                        {wrongAnswers.map((w, i) => (
                                            <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-500/20">
                                                <div className="text-sm text-gray-700 dark:text-gray-300 flex-1 mr-3 [&_p]:m-0">{renderContent(String(w.answer))}</div>
                                                <span className="shrink-0 text-xs font-semibold text-red-500">{w.count}×</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-400 italic">Aucune donnée de réponse.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Composant principal ─────────────────────────────────────────
export default function AdminContent({ isAdmin = true, user = null }) {
    const location = useLocation();
    const navigate = useNavigate();

    // Parser les segments d'URL depuis /apprendre/:subject/:chapter/notion/:notion-slug/edit
    const pathParts = location.pathname.split('/').filter(Boolean);
    const coursIndex = pathParts.indexOf('apprendre');
    const rawSegments = coursIndex >= 0 ? pathParts.slice(coursIndex + 1) : [];
    const segments = rawSegments[rawSegments.length - 1] === 'edit' ? rawSegments.slice(0, -1) : rawSegments;
    // segments: [subject, chapter, 'notion', notionSlug]  or  [subject, chapter]  or  [subject]
    const [subjectSlug, chapterSlug, , notionSlug] = segments;

    const [subjectsList, setSubjectsList] = useState([]);
    const [subjectsLoading, setSubjectsLoading] = useState(true);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [modal, setModal] = useState(null);
    const [selected, setSelected] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [localOrder, setLocalOrder] = useState([]);
    const [activeBlock, setActiveBlock] = useState(null);

    // Flashcards & Quizzes
    const [flashcards, setFlashcards] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [fcLoading, setFcLoading] = useState(false);
    const [qzLoading, setQzLoading] = useState(false);

    // Onglet actif dans la vue notion (mobile + desktop)
    const [notionTab, setNotionTab] = useState('cours');

    // Aperçu élève
    const [previewFc, setPreviewFc] = useState(null);
    const [previewQz, setPreviewQz] = useState(null);

    // Génération IA flashcards / quiz
    const [aiGeneratingFc, setAiGeneratingFc] = useState(false);
    const [aiResultFc, setAiResultFc] = useState(null);
    const [aiGeneratingQz, setAiGeneratingQz] = useState(false);
    const [aiResultQz, setAiResultQz] = useState(null);

    // Génération IA cours
    const [gcModal, setGcModal] = useState(false);
    const [gcTargetScore, setGcTargetScore] = useState(8);
    const [gcInstructions, setGcInstructions] = useState('');
    const [gcMode, setGcMode] = useState('complete'); // 'replace' | 'complete' | 'revise'
    const [gcGenerating, setGcGenerating] = useState(false);
    const [gcStatusText, setGcStatusText] = useState('Génération en cours...');
    const [gcValidating, setGcValidating] = useState(false);
    const [gcDraft, setGcDraft] = useState(null);
    const [gcDraftView, setGcDraftView] = useState('preview'); // 'preview' | 'raw'

    // Prérequis
    const [notionPrereqs, setNotionPrereqs] = useState([]);
    const [allNotionsForPrereqs, setAllNotionsForPrereqs] = useState([]);
    const [prereqsLoading, setPrereqsLoading] = useState(false);
    const [prereqSearch, setPrereqSearch] = useState('');

    // Déblocages manuels
    const [notionUnlocks, setNotionUnlocks] = useState([]);
    const [unlocksLoading, setUnlocksLoading] = useState(false);
    const [unlockSearch, setUnlockSearch] = useState('');
    const [allStudents, setAllStudents] = useState([]);

    const [statsNotionId, setStatsNotionId] = useState(null);
    const [statsQuizId, setStatsQuizId] = useState(null);
    const [statsFlashcardId, setStatsFlashcardId] = useState(null);
    const [, setNotionDiffCache] = useState({});
    const [quizStatsMap, setQuizStatsMap] = useState({});
    const [fcStatsMap, setFcStatsMap] = useState({});
    const loadedDiffIds = useRef(new Set());

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // ─── Bandeau ──────────────────────────────────────────────────
    const { setBandeau, resetBandeau } = useBandeau();
    useEffect(() => {
        const { chapters = [], notions = [] } = data ?? {};
        const currentSubject = findBySlug(subjectsList, subjectSlug);
        const currentChapter = findBySlug(chapters, chapterSlug);
        const currentNotion = findBySlug(notions, notionSlug);

        if (currentNotion) {
            setBandeau({
                title: currentNotion.name,
                breadcrumb: [
                    { label: 'Cours', onClick: () => navigate('/apprendre/edit') },
                    { label: currentSubject?.name || subjectSlug, onClick: () => navigate(`/apprendre/${subjectSlug}/edit`) },
                    { label: currentChapter?.name || chapterSlug, onClick: () => navigate(`/apprendre/${subjectSlug}/${chapterSlug}/edit`) },
                ],
            });
        } else if (currentChapter) {
            setBandeau({
                title: currentChapter.name,
                breadcrumb: [
                    { label: 'Cours', onClick: () => navigate('/apprendre/edit') },
                    { label: currentSubject?.name || subjectSlug, onClick: () => navigate(`/apprendre/${subjectSlug}/edit`) },
                ],
            });
        } else if (currentSubject) {
            setBandeau({ title: currentSubject.name, breadcrumb: [{ label: 'Cours', onClick: () => navigate('/apprendre/edit') }] });
        } else {
            setBandeau({ title: 'Administration — Cours', breadcrumb: null });
        }
        return () => resetBandeau();
    }, [subjectSlug, chapterSlug, notionSlug, data, subjectsList, setBandeau, resetBandeau, navigate]);

    const loadSubjects = useCallback(async () => {
        setSubjectsLoading(true);
        try {
            const res = await apiClient(`${BASE}/admin_content.php?action=list_subjects`);
            const json = await res.json();
            if (json.success) setSubjectsList(json.subjects);
        } finally { setSubjectsLoading(false); }
    }, []);

    const loadContent = useCallback(async (subjectId) => {
        if (!subjectId) return;
        setLoading(true);
        try {
            const res = await apiClient(`${BASE}/admin_content.php?action=list&subject_id=${subjectId}`);
            const json = await res.json();
            if (json.success) setData(json);
        } finally { setLoading(false); }
    }, []);

    // Charger la liste des matières au montage
    useEffect(() => { loadSubjects(); }, [loadSubjects]);

    // Auto-navigate to single subject si aucun sujet sélectionné et un seul disponible
    useEffect(() => {
        if (!subjectsLoading && subjectsList.length === 1 && !subjectSlug) {
            navigate(buildCoursePath(subjectsList[0], null, null, true), { replace: true });
        }
    }, [subjectsLoading, subjectsList, subjectSlug, navigate]);

    // Charger le contenu quand la matière change
    useEffect(() => {
        const selectedSubjectId = subjectSlug && subjectsList.length > 0
            ? (findBySlug(subjectsList, subjectSlug)?.id ?? 0)
            : 0;

        if (!selectedSubjectId) {
            setData(null);
            setSelected(null);
            return;
        }
        loadContent(selectedSubjectId);
    }, [subjectSlug, subjectsList, loadContent]);

    // ─── Récupération des données structurées
    const { subjects = [], chapters = [], levels = [], notions = [], courses = [] } = data ?? {};

    // Déterminer selected basé sur les slugs d'URL
    useEffect(() => {
        if (!data) {
            setSelected(null);
            return;
        }

        if (notionSlug) {
            const notion = findBySlug(notions, notionSlug);
            if (notion) {
                setSelected({ type: 'notion', id: notion.id });
                return;
            }
        }

        if (chapterSlug) {
            const chapter = findBySlug(chapters, chapterSlug);
            if (chapter) {
                setSelected({ type: 'chapter', id: chapter.id });
                return;
            }
        }

        setSelected(null);
    }, [chapterSlug, notionSlug, data, chapters, notions]);

    // Sync localOrder quand la notion ou les données changent
    useEffect(() => {
        if (selected?.type !== 'notion' || !data) { setLocalOrder([]); setSelectedIds(new Set()); return; }
        const blocks = (data.courses ?? [])
            .filter(c => c.notion_id === selected.id)
            .sort((a, b) => a.display_order - b.display_order);
        setLocalOrder(blocks.map(b => b.id));
        setSelectedIds(new Set());
    }, [selected?.id, selected?.type, data]);

    const toggleSelect = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const handleDragStart = ({ active }) => {
        setActiveBlock((data?.courses ?? []).find(b => b.id === active.id) ?? null);
    };

    const handleDragEnd = async ({ active, over }) => {
        setActiveBlock(null);
        if (!over) return;

        const overId = String(over.id);

        if (overId.startsWith('notion-')) {
            const targetNotionId = parseInt(overId.replace('notion-', ''));
            const selectedNotion = selected?.type === 'notion' ? (data?.notions ?? []).find(n => n.id === selected.id) : null;
            if (targetNotionId === selectedNotion?.id) return;
            const blockIds = selectedIds.has(active.id) && selectedIds.size > 1
                ? Array.from(selectedIds)
                : [active.id];
            await apiClient(`${BASE}/admin_content.php?action=move_blocks`, {
                method: 'POST',
                body: JSON.stringify({ notion_id: targetNotionId, block_ids: blockIds }),
            });
            setSelectedIds(new Set());
            await loadContent(selectedSubjectId);
            return;
        }

        if (active.id === over.id) return;
        const isMulti = selectedIds.has(active.id) && selectedIds.size > 1;
        let newOrder;

        if (isMulti) {
            if (selectedIds.has(over.id)) return;
            const sel = localOrder.filter(id => selectedIds.has(id));
            const unsel = localOrder.filter(id => !selectedIds.has(id));
            const activeIdx = localOrder.indexOf(active.id);
            const overIdx = localOrder.indexOf(over.id);
            const insertAt = activeIdx < overIdx
                ? unsel.indexOf(over.id) + 1
                : unsel.indexOf(over.id);
            newOrder = [...unsel.slice(0, insertAt), ...sel, ...unsel.slice(insertAt)];
        } else {
            newOrder = arrayMove(localOrder, localOrder.indexOf(active.id), localOrder.indexOf(over.id));
        }

        setLocalOrder(newOrder);
        await apiClient(`${BASE}/admin_content.php?action=reorder_blocks`, {
            method: 'POST',
            body: JSON.stringify({ order: newOrder.map((id, idx) => ({ id, display_order: idx })) }),
        });
    };

    const save = async (action, form) => {
        setBusy(true);
        const res = await apiClient(`${BASE}/admin_content.php?action=${action}`, {
            method: 'POST', body: JSON.stringify(form),
        });
        const json = await res.json();
        setBusy(false);
        if (json.success) {
            setModal(null);
            if (action === 'save_subject') {
                await loadSubjects();
                // Si nouvelle matière créée, naviguer vers elle
                if (!form.id && json.id) {
                    const newSubj = { id: json.id, name: form.name };
                    navigate(buildCoursePath(newSubj, null, null, true), { replace: true });
                }
            } else {
                await loadContent(selectedSubjectId);
            }
        } else {
            alert(json.message);
        }
    };

    const del = async (action, id, label) => {
        if (!window.confirm(`Supprimer "${label}" ? Cette action est irréversible.`)) return;
        setBusy(true);
        await apiClient(`${BASE}/admin_content.php?action=${action}`, {
            method: 'POST', body: JSON.stringify({ id }),
        });
        setBusy(false);
        if (action === 'delete_subject') {
            await loadSubjects();
            navigate('/apprendre/edit', { replace: true });
        } else {
            setSelected(null);
            await loadContent(selectedSubjectId);
        }
    };

    const generateMedia = async (block, mediaType, onProgress) => {
        const res = await apiClient(`${BASE}/generate_media.php`, {
            method: 'POST',
            body: JSON.stringify({ prompt: block.content, media_type: mediaType, course_id: block.id }),
        });
        const json = await res.json();
        if (!json.success) { alert(json.message); return; }
        if (!json.pending) { await loadContent(selectedSubjectId); return; }
        const { operation, course_id } = json;
        for (let i = 0; i < 36; i++) {
            await new Promise(r => setTimeout(r, 10000));
            onProgress?.(i + 1);
            const pollRes = await apiClient(`${BASE}/poll_media.php`, {
                method: 'POST',
                body: JSON.stringify({ operation, course_id }),
            });
            const poll = await pollRes.json();
            if (!poll.success) { alert(poll.message); return; }
            if (!poll.pending) { await loadContent(selectedSubjectId); return; }
        }
        alert('Délai de génération vidéo dépassé.');
    };

    const generateCourse = async () => {
        setGcGenerating(true);
        setGcDraft(null);
        setGcDraftView('preview');
        setGcStatusText("✦ Initialisation de la session...");
        try {
            let state = {
                step: 'init_plan',
                iteration: 0,
                notion_id: selectedNotion.id,
                mode: gcMode === 'revise' ? 'revise' : gcMode === 'complete' ? 'complete' : 'create',
                target_score: gcTargetScore,
                extra_instructions: gcInstructions.trim() || null
            };

            while (state.step !== 'done') {
                let statusText = "Génération en cours...";
                if (state.step === 'init_plan') {
                    statusText = "✦ Étape 1/5 : Conception du plan pédagogique...";
                } else if (state.step === 'init_draft') {
                    statusText = "✦ Étape 2/5 : Rédaction du premier brouillon...";
                } else if (state.step === 'critic') {
                    statusText = `✦ Étape 3/5 : Évaluation par le comité d'experts (Itér. ${state.iteration + 1})...`;
                } else if (state.step === 'refine_plan') {
                    statusText = `✦ Étape 4/5 : Affinement du plan selon les critiques (Itér. ${state.iteration + 1})...`;
                } else if (state.step === 'refine_draft') {
                    statusText = `✦ Étape 5/5 : Amélioration et polissage du texte (Itér. ${state.iteration})...`;
                }
                setGcStatusText(statusText);

                const res = await apiClient(`${BASE}/generate_course_step.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(state),
                });
                const json = await res.json();
                if (!json.success) {
                    throw new Error(json.error || 'Erreur lors de la génération par étape.');
                }
                state = json;
            }

            setGcDraft({
                success: true,
                notion_id: selectedNotion.id,
                plan: state.plan,
                draft: state.draft,
                iterations: state.iteration,
                converged: true,
                scores: state.scores
            });

        } catch (err) {
            console.error(err);
            alert('Erreur : ' + (err.message || 'Erreur de connexion.'));
        }
        setGcGenerating(false);
    };

    const validateCourse = async () => {
        if (!gcDraft) return;
        setGcValidating(true);
        try {
            const res = await apiClient(`${BASE}/generate_course.php?action=validate`, {
                method: 'POST',
                body: JSON.stringify({ notion_id: selectedNotion.id, draft: gcDraft.draft, replace: gcMode !== 'complete' }),
            });
            const json = await res.json();
            if (json.success) {
                setGcModal(false);
                setGcDraft(null);
                setGcInstructions('');
                setGcTargetScore(8);
                setGcMode('complete');
                await loadContent(selectedSubjectId);
            } else {
                alert('Erreur validation : ' + (json.error || 'Erreur inconnue'));
            }
        } catch {
            alert('Erreur de connexion.');
        }
        setGcValidating(false);
    };

    const delBlock = async (co) => {
        if (!window.confirm(`Supprimer ce bloc "${co.title || TYPE_LABELS[co.type] || co.type}" ?`)) return;
        setBusy(true);
        await apiClient(`${BASE}/admin_content.php?action=delete_course`, {
            method: 'POST', body: JSON.stringify({ id: co.id }),
        });
        await loadContent(selectedSubjectId);
        setBusy(false);
    };

    const publishBlock = async (blockId, action) => {
        await apiClient(`${BASE}/admin_content.php?action=${action}_block`, {
            method: 'POST', body: JSON.stringify({ id: blockId }),
        });
        await loadContent(selectedSubjectId);
    };

    const reloadFc = async (notionId) => {
        const r = await apiClient(`${BASE}/admin_content.php?action=list_flashcards&notion_id=${notionId}`);
        const d = await r.json();
        if (d.success) setFlashcards(d.flashcards);
    };

    const reloadQz = async (notionId) => {
        const r = await apiClient(`${BASE}/admin_content.php?action=list_quizzes&notion_id=${notionId}`);
        const d = await r.json();
        if (d.success) setQuizzes(d.quizzes);
    };

    const saveFc = async (form) => {
        setBusy(true);
        const res = await apiClient(`${BASE}/admin_content.php?action=save_flashcard`, {
            method: 'POST', body: JSON.stringify(form),
        });
        const json = await res.json();
        setBusy(false);
        if (json.success) { setModal(null); await reloadFc(form.notion_id); }
        else alert(json.message);
    };

    const delFc = async (fc) => {
        if (!window.confirm('Supprimer cette flashcard ?')) return;
        await apiClient(`${BASE}/admin_content.php?action=delete_flashcard`, {
            method: 'POST', body: JSON.stringify({ id: fc.id }),
        });
        setFlashcards(prev => prev.filter(f => f.id !== fc.id));
    };

    const saveQz = async (form) => {
        setBusy(true);
        const res = await apiClient(`${BASE}/admin_content.php?action=save_quiz`, {
            method: 'POST', body: JSON.stringify(form),
        });
        const json = await res.json();
        setBusy(false);
        if (json.success) { setModal(null); await reloadQz(form.notion_id); }
        else alert(json.message);
    };

    const delQz = async (qz) => {
        if (!window.confirm('Supprimer ce quiz ?')) return;
        await apiClient(`${BASE}/admin_content.php?action=delete_quiz`, {
            method: 'POST', body: JSON.stringify({ id: qz.id }),
        });
        setQuizzes(prev => prev.filter(q => q.id !== qz.id));
    };

    const goBackToSubjects = () => {
        navigate('/apprendre/edit', { replace: true });
    };

    // Charger les prérequis quand une notion est sélectionnée
    const selectedNotionForPrereqs = selected?.type === 'notion' ? selected.id : null;
    useEffect(() => {
        if (!selectedNotionForPrereqs || !isAdmin) { setNotionPrereqs([]); setAllNotionsForPrereqs([]); return; }
        setPrereqsLoading(true);
        setPrereqSearch('');
        apiClient(`${BASE}/admin_content.php?action=get_prerequisites&notion_id=${selectedNotionForPrereqs}`)
            .then(r => r.json())
            .then(d => {
                if (d.success) { setNotionPrereqs(d.prerequisites); setAllNotionsForPrereqs(d.all_notions); }
                setPrereqsLoading(false);
            })
            .catch(() => setPrereqsLoading(false));
    }, [selectedNotionForPrereqs, isAdmin]);

    const savePrereqs = async (newList) => {
        await apiClient(`${BASE}/admin_content.php?action=save_prerequisites`, {
            method: 'POST',
            body: JSON.stringify({ notion_id: selectedNotionForPrereqs, required_notion_ids: newList.map(p => p.required_notion_id) }),
        });
    };

    const addPrereq = async (notion) => {
        if (notionPrereqs.some(p => p.required_notion_id === notion.id)) return;
        const updated = [...notionPrereqs, { required_notion_id: notion.id, notion_name: notion.name, notion_number: notion.number, chapter_name: notion.chapter_name, subject_name: notion.subject_name }];
        setNotionPrereqs(updated);
        setPrereqSearch('');
        await savePrereqs(updated);
    };

    // Réinitialiser résultats IA et onglet quand la notion change
    useEffect(() => {
        setAiResultFc(null);
        setAiResultQz(null);
        setNotionTab('cours');
    }, [selectedNotionForPrereqs]);

    const generateWithAI = async (notionId, type) => {
        const setGenerating = type === 'flashcards' ? setAiGeneratingFc : setAiGeneratingQz;
        const setResult = type === 'flashcards' ? setAiResultFc : setAiResultQz;
        setGenerating(true);
        setResult(null);
        try {
            const res = await apiClient(`${BASE}/admin_generate_content.php`, {
                method: 'POST',
                body: JSON.stringify({ notion_id: notionId, type }),
            });
            const json = await res.json();
            if (json.success) {
                setResult({ ok: true, text: json.message });
                if (type === 'flashcards') await reloadFc(notionId);
                else await reloadQz(notionId);
            } else {
                setResult({ ok: false, text: json.message });
            }
        } catch {
            setResult({ ok: false, text: 'Erreur de connexion.' });
        }
        setGenerating(false);
    };

    // Charger flashcards & quizzes quand une notion est sélectionnée
    useEffect(() => {
        if (!selectedNotionForPrereqs) { setFlashcards([]); setQuizzes([]); return; }
        setFcLoading(true);
        setQzLoading(true);
        apiClient(`${BASE}/admin_content.php?action=list_flashcards&notion_id=${selectedNotionForPrereqs}`)
            .then(r => r.json())
            .then(d => { if (d.success) setFlashcards(d.flashcards); setFcLoading(false); })
            .catch(() => setFcLoading(false));
        apiClient(`${BASE}/admin_content.php?action=list_quizzes&notion_id=${selectedNotionForPrereqs}`)
            .then(r => r.json())
            .then(d => { if (d.success) setQuizzes(d.quizzes); setQzLoading(false); })
            .catch(() => setQzLoading(false));
    }, [selectedNotionForPrereqs]);

    // Charger les stats quiz + flashcard en arrière-plan (mini-barres + modals)
    useEffect(() => {
        if (!selectedNotionForPrereqs || !isAdmin) return;
        if (loadedDiffIds.current.has(selectedNotionForPrereqs)) return;
        loadedDiffIds.current.add(selectedNotionForPrereqs);
        const nid = selectedNotionForPrereqs;
        Promise.all([
            apiClient(`${BASE}/admin_stats.php?action=quiz&notion_id=${nid}`).then(r => r.json()),
            apiClient(`${BASE}/admin_stats.php?action=flashcard&notion_id=${nid}`).then(r => r.json()),
        ]).then(([qJson, fJson]) => {
            if (qJson.success) {
                const ratio = calcDifficultyRatio(qJson.data);
                if (ratio) setNotionDiffCache(p => ({ ...p, [nid]: ratio }));
                setQuizStatsMap(p => {
                    const next = { ...p };
                    qJson.data.forEach(q => { next[q.id] = q; });
                    return next;
                });
            }
            if (fJson.success) {
                setFcStatsMap(p => {
                    const next = { ...p };
                    fJson.data.forEach(f => { next[f.id] = f; });
                    return next;
                });
            }
        }).catch(() => { loadedDiffIds.current.delete(nid); });
    }, [selectedNotionForPrereqs, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

    // Charger la liste des étudiants une seule fois (pour la recherche d'unlock)
    useEffect(() => {
        if (!isAdmin) return;
        apiClient(`${BASE}/admin_content.php?action=list_students`)
            .then(r => r.json())
            .then(d => { if (d.success) setAllStudents(d.students); });
    }, [isAdmin]);

    // Charger les déblocages manuels quand une notion est sélectionnée
    useEffect(() => {
        if (!selectedNotionForPrereqs || !isAdmin) { setNotionUnlocks([]); return; }
        setUnlocksLoading(true);
        setUnlockSearch('');
        apiClient(`${BASE}/admin_content.php?action=get_notion_unlocks&notion_id=${selectedNotionForPrereqs}`)
            .then(r => r.json())
            .then(d => {
                if (d.success) setNotionUnlocks(d.unlocks);
                setUnlocksLoading(false);
            })
            .catch(() => setUnlocksLoading(false));
    }, [selectedNotionForPrereqs, isAdmin]);

    const unlockNotion = async (student) => {
        await apiClient(`${BASE}/admin_content.php?action=unlock_notion`, {
            method: 'POST',
            body: JSON.stringify({ notion_id: selectedNotionForPrereqs, student_id: student.id }),
        });
        setNotionUnlocks(prev => [...prev, { student_id: student.id, student_name: student.name, email: student.email }]);
        setUnlockSearch('');
    };

    const revokeUnlock = async (studentId) => {
        await apiClient(`${BASE}/admin_content.php?action=revoke_unlock`, {
            method: 'POST',
            body: JSON.stringify({ notion_id: selectedNotionForPrereqs, student_id: studentId }),
        });
        setNotionUnlocks(prev => prev.filter(u => u.student_id !== studentId));
    };

    const removePrereq = async (requiredNotionId) => {
        const updated = notionPrereqs.filter(p => p.required_notion_id !== requiredNotionId);
        setNotionPrereqs(updated);
        await savePrereqs(updated);
    };

    // Calculer selectedSubjectId basé sur le slug
    const selectedSubjectId = subjectSlug && subjectsList.length > 0
        ? (findBySlug(subjectsList, subjectSlug)?.id ?? 0)
        : 0;

    // ── Écran de sélection de matière ───────────────────────────────
    if (!selectedSubjectId) {
        if (subjectsLoading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

        return (
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Choisir une matière</h2>
                    {isAdmin && (
                        <ActionBtn variant="add" onClick={() => setModal({ type: 'subject', form: { ...EMPTY_SUBJECT } })}>+ Ajouter une matière</ActionBtn>
                    )}
                </div>

                {subjectsList.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">Aucune matière disponible.</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {subjectsList.map(s => (
                            <button
                                key={s.id}
                                onClick={() => navigate(buildCoursePath(s, null, null, true))}
                                className="flex flex-col items-start gap-2 p-5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 hover:border-cyan-400 dark:hover:border-cyan-500 hover:shadow-md transition-all text-left group">
                                <SubjectIcon subject={s.name} className="w-8 h-8" />
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-cyan-600 dark:group-hover:text-cyan-400">{s.name}</span>
                            </button>
                        ))}
                    </div>
                )}

                {modal?.type === 'subject' && (
                    <SubjectModal form={modal.form} busy={busy} onClose={() => setModal(null)} onSave={f => save('save_subject', f)} />
                )}
            </div>
        );
    }

    // ── Vue contenu d'une matière ────────────────────────────────────
    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    const currentSubject = subjects.find(s => s.id === selectedSubjectId)
        ?? subjectsList.find(s => s.id === selectedSubjectId);
    const selectedChapter = selected?.type === 'chapter' ? chapters.find(c => c.id === selected.id) : null;
    const selectedNotion = selected?.type === 'notion' ? notions.find(n => n.id === selected.id) : null;

    const notionsForChapter = (chId) => {
        const ids = levels.filter(l => l.chapter_id === chId).map(l => l.id);
        return notions.filter(n => ids.includes(n.chapter_level_id));
    };
    const blocksForNotion = (nid) => courses.filter(c => c.notion_id === nid)
        .sort((a, b) => a.display_order - b.display_order);
    const levelOfNotion = (n) => levels.find(l => l.id === n.chapter_level_id);

    const currentSubj = subjects.find(s => s.id === selectedSubjectId) ?? subjectsList.find(s => s.id === selectedSubjectId);

    const selectToggle = (type, item) => {
        if (type === 'chapter') {
            if (selected?.type === 'chapter' && selected?.id === item.id) {
                navigate(buildCoursePath(currentSubj, null, null, true), { replace: true });
            } else {
                navigate(buildCoursePath(currentSubj, item, null, true), { replace: true });
            }
        } else if (type === 'notion') {
            const notionLevel = levels.find(l => l.id === item.chapter_level_id);
            const notionChapter = notionLevel ? chapters.find(c => c.id === notionLevel.chapter_id) : null;
            if (selected?.type === 'notion' && selected?.id === item.id) {
                navigate(buildCoursePath(currentSubj, notionChapter, null, true), { replace: true });
            } else {
                navigate(buildCoursePath(currentSubj, notionChapter, item, true), { replace: true });
            }
        }
    };

    // ── Données de la notion sélectionnée (pour la barre du haut) ──────────
    const notionLv = selectedNotion ? levelOfNotion(selectedNotion) : null;
    const notionChap = notionLv ? chapters.find(c => c.id === notionLv.chapter_id) : null;
    const notionBlocks = selectedNotion ? blocksForNotion(selectedNotion.id) : [];
    const viewUrl = notionChap && selectedNotion
        ? `/apprendre/${currentSubj?.slug || generateSlug(currentSubj?.name || '')}/${notionChap.slug || generateSlug(notionChap.name)}/lecon#${selectedNotion.slug || generateSlug(selectedNotion.name)}`
        : null;
    const notionTabs = selectedNotion ? [
        { id: 'cours', label: `Cours (${notionBlocks.length})` },
        { id: 'flashcards', label: `Flashcards (${flashcards.length})` },
        { id: 'quiz', label: `Quiz (${quizzes.length})` },
        ...(isAdmin ? [{ id: 'prereqs', label: 'Prérequis' }, { id: 'deblocages', label: 'Déblocages' }] : []),
    ] : [];

    // ── Données du chapitre sélectionné (pour la barre du haut) ───────────
    const viewChapUrl = selectedChapter
        ? `/apprendre/${currentSubj?.slug || generateSlug(currentSubj?.name || '')}/${selectedChapter.slug || generateSlug(selectedChapter.name)}/lecon`
        : null;

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex flex-col gap-4 h-full min-h-0">

                {/* ── Barre du haut : onglets + navigation + actions ── */}
                {selectedNotion && (
                    <div className="flex items-center justify-between gap-3 shrink-0 flex-wrap">
                        <TabBar tabs={notionTabs} active={notionTab} onChange={setNotionTab} />
                        <div className="flex gap-1.5 items-center">
                            {viewUrl && (
                                <ActionBtn variant="view" onClick={() => navigate(viewUrl)}
                                    icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}>
                                    Voir
                                </ActionBtn>
                            )}
                            {isAdmin && (
                                <ActionBtn onClick={() => setModal({ type: 'notion', form: { ...selectedNotion, chapter_id: notionLv?.chapter_id } })}>Modifier</ActionBtn>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Barre chapitre : navigation + actions ── */}
                {selectedChapter && !selectedNotion && (
                    <div className="flex items-center justify-between gap-3 shrink-0 flex-wrap">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 px-1 flex items-center gap-1.5">
                            <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                            <span>{selectedChapter.name}</span>
                        </span>
                        <div className="flex gap-1.5 items-center">
                            {viewChapUrl && (
                                <ActionBtn variant="view" onClick={() => navigate(viewChapUrl)}
                                    icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}>
                                    Voir
                                </ActionBtn>
                            )}
                            {isAdmin && (
                                <>
                                    <ActionBtn onClick={() => setModal({ type: 'chapter', form: { ...selectedChapter } })}>Modifier</ActionBtn>
                                    <ActionBtn variant="add" onClick={() => setModal({ type: 'notion', form: { ...EMPTY_NOTION, chapter_id: selectedChapter.id } })}>+ Notion</ActionBtn>
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
                    {/* ── Arbre gauche ── */}
                    <div className="w-full md:w-72 md:shrink-0 overflow-y-auto space-y-1">
                        {/* Retour + nom de la matière */}
                        {subjectsList.length > 1 && (
                            <button
                                onClick={goBackToSubjects}
                                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mb-2 px-1 transition-colors">
                                ← Toutes les matières
                            </button>
                        )}
                        <div className="flex items-center justify-between mb-3 px-1">
                            <div className="flex items-center gap-2 min-w-0">
                                <SubjectIcon subject={currentSubject?.name} className="w-5 h-5 shrink-0" />
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                    {currentSubject?.name}
                                </span>
                            </div>
                            {isAdmin && currentSubject && (
                                <div className="flex gap-1 shrink-0 ml-2">
                                    <ActionBtn onClick={() => setModal({ type: 'subject', form: { ...currentSubject, weight_notion: currentSubject.weight_comprehension ?? 60, weight_exercice: currentSubject.weight_application ?? 40, mastery_threshold: currentSubject.mastery_threshold ?? 80, new_items_per_day: currentSubject.new_items_per_day ?? 10 } })}>✎</ActionBtn>
                                    <ActionBtn variant="danger" onClick={() => del('delete_subject', currentSubject.id, currentSubject.name)}>✕</ActionBtn>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between mb-2 px-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Chapitres</span>
                            {isAdmin && (
                                <ActionBtn onClick={() => setModal({ type: 'chapter', form: { ...EMPTY_CHAPTER, subject_id: selectedSubjectId } })}>+ Ajouter</ActionBtn>
                            )}
                        </div>

                        {chapters.map(chap => (
                            <div key={chap.id}>
                                <button
                                    onClick={() => selectToggle('chapter', chap)}
                                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${selected?.id === chap.id && selected?.type === 'chapter' ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'}`}
                                >
                                    <span className="flex items-center gap-1.5">
                                        <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                                        <span className="truncate">{chap.name}</span>
                                    </span>
                                </button>
                                {notionsForChapter(chap.id).map(notion => (
                                    <DroppableNotion
                                        key={notion.id}
                                        notion={notion}
                                        isSelected={selected?.id === notion.id && selected?.type === 'notion'}
                                        onClick={() => selectToggle('notion', notion)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* ── Panneau droit ── */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Vue d'ensemble de la matière (rien de sélectionné) */}
                        {!selected && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <SubjectIcon subject={currentSubject?.name} className="w-7 h-7" />
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{currentSubject?.name}</h3>
                                    </div>
                                    {isAdmin && currentSubject && (
                                        <div className="flex gap-2">
                                            <ActionBtn onClick={() => setModal({ type: 'subject', form: { ...currentSubject, weight_notion: currentSubject.weight_comprehension ?? 60, weight_exercice: currentSubject.weight_application ?? 40, mastery_threshold: currentSubject.mastery_threshold ?? 80, new_items_per_day: currentSubject.new_items_per_day ?? 10 } })}>Modifier</ActionBtn>
                                            <ActionBtn variant="danger" onClick={() => del('delete_subject', currentSubject.id, currentSubject.name)}>Supprimer</ActionBtn>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500">{chapters.length} chapitre(s)</span>
                                    {isAdmin && (
                                        <ActionBtn variant="primary" onClick={() => setModal({ type: 'chapter', form: { ...EMPTY_CHAPTER, subject_id: selectedSubjectId } })}>
                                            + Ajouter un chapitre
                                        </ActionBtn>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {chapters.map(ch => (
                                        <div key={ch.id}
                                            onClick={() => selectToggle('chapter', ch)}
                                            className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                            <div>
                                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                    <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                                                    <span>{ch.name}</span>
                                                </span>
                                                {ch.display_group && <span className="ml-2 text-xs text-gray-400">({ch.display_group})</span>}
                                            </div>
                                            <span className="text-xs text-gray-400">{notionsForChapter(ch.id).length} notion(s)</span>
                                        </div>
                                    ))}
                                    {chapters.length === 0 && (
                                        <p className="text-sm text-gray-400 italic text-center py-8">
                                            Aucun chapitre.{isAdmin ? ' Ajoute-en un depuis l\'arbre à gauche.' : ''}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Détail Chapitre ── */}
                        {selectedChapter && (() => {
                            return (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        {notionsForChapter(selectedChapter.id).map(n => {
                                            const lv = levelOfNotion(n);
                                            return (
                                                <div key={n.id}
                                                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    onClick={() => selectToggle('notion', n)}>
                                                    <span className="text-sm text-gray-800 dark:text-gray-200">◆ {n.number} {n.name}</span>
                                                    <span className="text-xs text-gray-400">{lv?.school_year} • {blocksForNotion(n.id).length} bloc(s)</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ── Détail Notion — onglets ── */}
                        {selectedNotion && (() => {
                            const blocks = blocksForNotion(selectedNotion.id);

                            return (
                                <div className="space-y-4">
                                    {/* ── Onglet Cours ── */}
                                    {notionTab === 'cours' && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-500">{blocks.length} bloc(s)</span>
                                                <div className="flex items-center gap-2">
                                                    <ActionBtn onClick={() => { 
                                                        setGcDraft(null); 
                                                        setGcMode(blocks.length > 0 ? 'complete' : 'replace');
                                                        setGcModal(true); 
                                                    }}>✦ Générer IA</ActionBtn>
                                                    <ActionBtn variant="primary" onClick={() => setModal({
                                                        type: 'block',
                                                        form: { ...EMPTY_COURSE, notion_id: selectedNotion.id, display_order: blocks.length },
                                                    })}>+ Ajouter un bloc</ActionBtn>
                                                </div>
                                            </div>
                                            {selectedIds.size > 0 && (
                                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                                    {selectedIds.size} bloc(s) sélectionné(s) — glisse depuis ⠿ pour les déplacer ensemble
                                                </p>
                                            )}
                                            {blocks.length === 0 && (
                                                <p className="text-sm text-gray-400 italic text-center py-8">
                                                    Aucun bloc. Génère un cours IA ou ajoute un bloc manuellement.
                                                </p>
                                            )}
                                            <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
                                                <div className="space-y-2">
                                                    {localOrder.map((id, idx) => {
                                                        const co = blocks.find(b => b.id === id);
                                                        if (!co) return null;
                                                        return (
                                                            <SortableBlock
                                                                key={co.id}
                                                                block={co}
                                                                idx={idx}
                                                                isSelected={selectedIds.has(co.id)}
                                                                onToggleSelect={toggleSelect}
                                                                onEdit={co => setModal({ type: 'block', form: { ...co } })}
                                                                onDelete={delBlock}
                                                                onGenerate={generateMedia}
                                                                onPublish={publishBlock}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </SortableContext>
                                        </div>
                                    )}

                                    {/* ── Onglet Flashcards ── */}
                                    {notionTab === 'flashcards' && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                    Flashcards <span className="text-gray-400 font-normal">({flashcards.length})</span>
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    {fcLoading && <span className="text-xs text-gray-400">Chargement…</span>}
                                                    <ActionBtn disabled={aiGeneratingFc} onClick={() => generateWithAI(selectedNotion.id, 'flashcards')}>
                                                        {aiGeneratingFc ? '⏳…' : '✦ IA'}
                                                    </ActionBtn>
                                                    <ActionBtn variant="primary" onClick={() => setModal({ type: 'flashcard', form: { id: null, notion_id: selectedNotion.id, question: '', answer: '' } })}>+ Ajouter</ActionBtn>
                                                </div>
                                            </div>
                                            {aiResultFc && (
                                                <p className={`text-xs ${aiResultFc.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                                                    {aiResultFc.text}
                                                </p>
                                            )}
                                            <div className="space-y-2">
                                                {flashcards.map(fc => {
                                                    const stat = isAdmin ? fcStatsMap[fc.id] : null;
                                                    const t = stat?.total_attempts ?? 0;
                                                    return (
                                                        <div key={fc.id}
                                                            onClick={() => setPreviewFc(fc)}
                                                            className="rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden cursor-pointer hover:border-fuchsia-300 dark:hover:border-fuchsia-700 transition-colors">
                                                            <div className="flex items-start justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60">
                                                                <div className="text-sm text-gray-800 dark:text-gray-200 font-medium flex-1 mr-3 [&_p]:m-0 [&_p+p]:mt-1">{renderContent(fc.question)}</div>
                                                                <div className="flex gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                                                    {isAdmin && (
                                                                        <button
                                                                            onClick={() => setStatsFlashcardId(fc.id)}
                                                                            className="flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-lg border border-fuchsia-200 dark:border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-400 text-xs font-semibold hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 transition-colors min-w-[42px]">
                                                                            <span>Stats</span>
                                                                            <div className="w-full h-1 rounded-full overflow-hidden flex bg-gray-200 dark:bg-white/10">
                                                                                {t > 0 ? (
                                                                                    <>
                                                                                        <div className="bg-red-400 h-full" style={{ width: `${Math.round((stat.hard_count ?? 0) / t * 100)}%` }} />
                                                                                        <div className="bg-orange-400 h-full" style={{ width: `${Math.round((stat.medium_count ?? 0) / t * 100)}%` }} />
                                                                                        <div className="bg-emerald-400 h-full" style={{ width: `${Math.round((stat.easy_count ?? 0) / t * 100)}%` }} />
                                                                                    </>
                                                                                ) : null}
                                                                            </div>
                                                                        </button>
                                                                    )}
                                                                    <ActionBtn onClick={() => setModal({ type: 'flashcard', form: { ...fc } })}>Modifier</ActionBtn>
                                                                    <ActionBtn variant="danger" onClick={() => delFc(fc)}>✕</ActionBtn>
                                                                </div>
                                                            </div>
                                                            <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-white/5 [&_p]:m-0 [&_p+p]:mt-1">
                                                                {renderContent(fc.answer)}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {flashcards.length === 0 && !fcLoading && (
                                                    <p className="text-xs text-gray-400 italic">Aucune flashcard pour cette notion.</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Onglet Quiz ── */}
                                    {notionTab === 'quiz' && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                    Quiz <span className="text-gray-400 font-normal">({quizzes.length})</span>
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    {qzLoading && <span className="text-xs text-gray-400">Chargement…</span>}
                                                    <ActionBtn disabled={aiGeneratingQz} onClick={() => generateWithAI(selectedNotion.id, 'quizzes')}>
                                                        {aiGeneratingQz ? '⏳…' : '✦ IA'}
                                                    </ActionBtn>
                                                    <ActionBtn variant="primary" onClick={() => setModal({ type: 'quiz', form: { id: null, notion_id: selectedNotion.id, question: '', answer: null } })}>+ Ajouter</ActionBtn>
                                                </div>
                                            </div>
                                            {aiResultQz && (
                                                <p className={`text-xs ${aiResultQz.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                                                    {aiResultQz.text}
                                                </p>
                                            )}
                                            <div className="space-y-2">
                                                {quizzes.map(qz => {
                                                    const qType = qz.answer?.type ?? 'single_choice';
                                                    const stat = isAdmin ? quizStatsMap[qz.id] : null;
                                                    const t = stat?.total_attempts ?? 0;
                                                    return (
                                                        <div key={qz.id}
                                                            onClick={() => setPreviewQz(qz)}
                                                            className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-800/60 cursor-pointer hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors">
                                                            <div className="flex items-start gap-2 min-w-0 flex-1 mr-3">
                                                                <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${QUIZ_TYPE_COLORS[qType] ?? ''}`}>
                                                                    {QUIZ_TYPE_LABELS[qType] ?? qType}
                                                                </span>
                                                                <div className="text-sm text-gray-700 dark:text-gray-300 min-w-0 [&_p]:m-0 [&_p+p]:mt-1">{renderContent(qz.question)}</div>
                                                            </div>
                                                            <div className="flex gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                                                {isAdmin && (
                                                                    <button
                                                                        onClick={() => setStatsQuizId(qz.id)}
                                                                        className="flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-lg border border-fuchsia-200 dark:border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-400 text-xs font-semibold hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 transition-colors min-w-[42px]">
                                                                        <span>Stats</span>
                                                                        <div className="w-full h-1 rounded-full overflow-hidden flex bg-gray-200 dark:bg-white/10">
                                                                            {t > 0 ? (
                                                                                <>
                                                                                    <div className="bg-red-400 h-full" style={{ width: `${Math.round((stat.hard_count ?? 0) / t * 100)}%` }} />
                                                                                    <div className="bg-orange-400 h-full" style={{ width: `${Math.round((stat.medium_count ?? 0) / t * 100)}%` }} />
                                                                                    <div className="bg-emerald-400 h-full" style={{ width: `${Math.round((stat.easy_count ?? 0) / t * 100)}%` }} />
                                                                                </>
                                                                            ) : null}
                                                                        </div>
                                                                    </button>
                                                                )}
                                                                <ActionBtn onClick={() => setModal({ type: 'quiz', form: { ...qz } })}>Modifier</ActionBtn>
                                                                <ActionBtn variant="danger" onClick={() => delQz(qz)}>✕</ActionBtn>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {quizzes.length === 0 && !qzLoading && (
                                                    <p className="text-xs text-gray-400 italic">Aucun quiz pour cette notion.</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Onglet Prérequis ── */}
                                    {notionTab === 'prereqs' && isAdmin && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Prérequis</span>
                                                {prereqsLoading && <span className="text-xs text-gray-400">Chargement…</span>}
                                            </div>
                                            <p className="text-xs text-gray-400">L'élève doit maîtriser ces notions avant d'accéder à celle-ci.</p>
                                            <div className="space-y-1.5">
                                                {notionPrereqs.map(p => (
                                                    <div key={p.required_notion_id} className="flex items-center justify-between px-3 py-1.5 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800/30">
                                                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                                            <span className="font-medium">{p.notion_number} {p.notion_name}</span>
                                                            <span className="text-gray-400 ml-1">— {p.chapter_name} ({p.subject_name})</span>
                                                        </span>
                                                        <button onClick={() => removePrereq(p.required_notion_id)} className="shrink-0 ml-2 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                                                    </div>
                                                ))}
                                                {notionPrereqs.length === 0 && !prereqsLoading && (
                                                    <p className="text-xs text-gray-400 italic">Aucun prérequis configuré.</p>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <input
                                                    value={prereqSearch}
                                                    onChange={e => setPrereqSearch(e.target.value)}
                                                    placeholder="Rechercher une notion à ajouter…"
                                                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 text-gray-800 dark:text-gray-200"
                                                />
                                                {prereqSearch.length >= 2 && (() => {
                                                    const q = prereqSearch.toLowerCase();
                                                    const filtered = allNotionsForPrereqs.filter(n =>
                                                        !notionPrereqs.some(p => p.required_notion_id === n.id) &&
                                                        (n.name.toLowerCase().includes(q) || n.chapter_name.toLowerCase().includes(q) || n.subject_name.toLowerCase().includes(q))
                                                    );
                                                    return (
                                                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg shadow-lg max-h-44 overflow-y-auto">
                                                            {filtered.length === 0
                                                                ? <p className="px-3 py-2 text-xs text-gray-400">Aucun résultat</p>
                                                                : filtered.map(n => (
                                                                    <button key={n.id} onClick={() => addPrereq(n)}
                                                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-white/5 last:border-0">
                                                                        <span className="font-medium">{n.number} {n.name}</span>
                                                                        <span className="text-gray-400 ml-1">— {n.subject_name} / {n.chapter_name}</span>
                                                                    </button>
                                                                ))
                                                            }
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Onglet Déblocages ── */}
                                    {notionTab === 'deblocages' && isAdmin && (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Déblocages manuels</span>
                                                {unlocksLoading && <span className="text-xs text-gray-400">Chargement…</span>}
                                            </div>
                                            <p className="text-xs text-gray-400">Accorder l'accès à cette notion sans que les prérequis soient maîtrisés.</p>
                                            <div className="space-y-1.5">
                                                {notionUnlocks.map(u => (
                                                    <div key={u.student_id} className="flex items-center justify-between px-3 py-1.5 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800/30">
                                                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                                            <span className="font-medium">{u.student_name}</span>
                                                            <span className="text-gray-400 ml-1">— {u.email}</span>
                                                        </span>
                                                        <button onClick={() => revokeUnlock(u.student_id)} className="shrink-0 ml-2 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                                                    </div>
                                                ))}
                                                {notionUnlocks.length === 0 && !unlocksLoading && (
                                                    <p className="text-xs text-gray-400 italic">Aucun déblocage manuel.</p>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <input
                                                    value={unlockSearch}
                                                    onChange={e => setUnlockSearch(e.target.value)}
                                                    placeholder="Rechercher un élève à débloquer…"
                                                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 text-gray-800 dark:text-gray-200"
                                                />
                                                {unlockSearch.length >= 2 && (() => {
                                                    const q = unlockSearch.toLowerCase();
                                                    const filtered = allStudents.filter(s =>
                                                        !notionUnlocks.some(u => u.student_id === s.id) &&
                                                        (s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q))
                                                    );
                                                    return (
                                                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg shadow-lg max-h-44 overflow-y-auto">
                                                            {filtered.length === 0
                                                                ? <p className="px-3 py-2 text-xs text-gray-400">Aucun résultat</p>
                                                                : filtered.map(s => (
                                                                    <button key={s.id} onClick={() => unlockNotion(s)}
                                                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-white/5 last:border-0">
                                                                        <span className="font-medium">{s.name}</span>
                                                                        <span className="text-gray-400 ml-1">— {s.email}</span>
                                                                    </button>
                                                                ))
                                                            }
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* ── Modal stats flashcard ── */}
                {statsFlashcardId && (() => {
                    const fc = flashcards.find(f => f.id === statsFlashcardId);
                    return fc ? (
                        <FlashcardStatsModal
                            fc={fc}
                            notionId={fc.notion_id}
                            onClose={() => setStatsFlashcardId(null)}
                        />
                    ) : null;
                })()}

                {/* ── Modal stats quiz ── */}
                {statsQuizId && (() => {
                    const qz = quizzes.find(q => q.id === statsQuizId);
                    return qz ? (
                        <QuizStatsModal
                            quiz={qz}
                            notionId={qz.notion_id}
                            onClose={() => setStatsQuizId(null)}
                        />
                    ) : null;
                })()}

                {/* ── Modal stats notion ── */}
                {statsNotionId && (() => {
                    const n = notions.find(x => x.id === statsNotionId);
                    return n ? (
                        <NotionStatsModal
                            notionId={statsNotionId}
                            notionName={`${n.number} ${n.name}`}
                            onClose={() => setStatsNotionId(null)}
                        />
                    ) : null;
                })()}

                {/* ── Modales ── */}
                {modal?.type === 'subject' && (
                    <SubjectModal form={modal.form} busy={busy} onClose={() => setModal(null)} onSave={f => save('save_subject', f)} />
                )}
                {modal?.type === 'chapter' && (
                    <ChapterModal form={modal.form} subjects={subjects.length ? subjects : subjectsList} busy={busy} onClose={() => setModal(null)} onSave={f => save('save_chapter', f)}
                        onDelete={modal.form?.id ? () => { setModal(null); del('delete_chapter', modal.form.id, modal.form.name); } : null} />
                )}
                {modal?.type === 'notion' && (
                    <NotionModal form={modal.form} chapters={chapters} busy={busy} onClose={() => setModal(null)} onSave={f => save('save_notion', f)}
                        onDelete={modal.form?.id ? () => { setModal(null); del('delete_notion', modal.form.id, modal.form.name); } : null} />
                )}
                {modal?.type === 'block' && (
                    <BlockModal form={modal.form} busy={busy} onClose={() => setModal(null)} onSave={f => save('save_course', f)} />
                )}
                {modal?.type === 'flashcard' && (
                    <FlashcardModal form={modal.form} busy={busy} onClose={() => setModal(null)} onSave={saveFc} />
                )}
                {modal?.type === 'quiz' && (
                    <QuizModal form={modal.form} busy={busy} onClose={() => setModal(null)} onSave={saveQz} />
                )}

                {gcModal && (
                    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
                                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">✦ Générer le cours avec l'IA</h2>
                                <button onClick={() => { if (!gcGenerating && !gcValidating) { setGcModal(false); setGcDraft(null); } }} className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-xl leading-none">✕</button>
                            </div>

                            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
                                {/* Étape 1 : config */}
                                {!gcGenerating && !gcDraft && (
                                    <>
                                        {(() => {
                                            const existingCount = blocksForNotion(selectedNotion?.id).length;
                                            return existingCount > 0 ? (
                                                <div className="rounded-xl border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5 overflow-hidden">
                                                    {[
                                                        { value: 'complete', label: 'Compléter le cours existant', desc: `L'IA lit les ${existingCount} blocs, identifie ce qui manque et rédige uniquement les sections absentes dans le même ton.`, color: 'fuchsia' },
                                                        { value: 'revise', label: 'Réviser le cours existant', desc: `L'IA lit les ${existingCount} blocs, améliore la clarté, les exemples et la pédagogie, et réécrit le cours complet.`, color: 'emerald' },
                                                        { value: 'replace', label: 'Nouveau cours depuis zéro', desc: 'Génère un cours complet sans tenir compte du contenu existant. Remplace tous les blocs.', color: 'red' },
                                                    ].map(opt => (
                                                        <label key={opt.value} className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${gcMode === opt.value ? 'bg-gray-50 dark:bg-white/[0.04]' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'}`}>
                                                            <input type="radio" name="gcMode" value={opt.value} checked={gcMode === opt.value} onChange={() => setGcMode(opt.value)} className={`mt-0.5 accent-${opt.color}-500`} />
                                                            <div>
                                                                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{opt.label}</p>
                                                                <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : null;
                                        })()}
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                                                Score cible : <span className="text-fuchsia-600 dark:text-fuchsia-400">{gcTargetScore}/10</span>
                                                <span className="font-normal text-gray-400 ml-2">(max 5 itérations)</span>
                                            </label>
                                            <input
                                                type="range" min={5} max={10} step={0.5}
                                                value={gcTargetScore}
                                                onChange={e => setGcTargetScore(Number(e.target.value))}
                                                className="w-full accent-fuchsia-500"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>5 (rapide)</span><span>7.5 (équilibré)</span><span>10 (approfondi)</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                                                Instructions supplémentaires <span className="font-normal">(optionnel)</span>
                                            </label>
                                            <textarea
                                                rows={4}
                                                value={gcInstructions}
                                                onChange={e => setGcInstructions(e.target.value)}
                                                placeholder={"Ex : Insiste sur les démonstrations, ajoute des exemples tirés de la physique, niveau 1ère S…"}
                                                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 resize-none"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-400">
                                            La génération itère jusqu'à atteindre le score cible (5 itérations max). Prévoir <strong>1–4 minutes</strong> selon la difficulté.
                                        </p>
                                    </>
                                )}

                                {/* Étape 2 : chargement */}
                                {gcGenerating && (
                                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                                        <div className="w-10 h-10 border-4 border-fuchsia-500 border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center font-medium">
                                            {gcStatusText}
                                            <br /><span className="text-xs text-gray-400 block mt-1">Score cible : {gcTargetScore}/10</span>
                                        </p>
                                    </div>
                                )}

                                {/* Étape 3 : brouillon */}
                                {gcDraft && !gcGenerating && (
                                    <div className="space-y-3">
                                        {/* Barre d'infos + toggle */}
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">✓ Brouillon généré</span>
                                            {gcDraft.scores?.length > 0 && (
                                                <span className="text-xs text-gray-400">
                                                    Score : {gcDraft.scores[gcDraft.scores.length - 1]?.average}/10{gcDraft.converged ? ' · convergé' : ''}
                                                </span>
                                            )}
                                            <div className="ml-auto flex items-center gap-1">
                                                <button
                                                    onClick={() => setGcDraftView('preview')}
                                                    className={`text-xs px-2.5 py-1 rounded-l-lg border transition-colors ${gcDraftView === 'preview' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-gray-300 dark:border-white/20 text-gray-500 hover:border-fuchsia-400'}`}
                                                >Aperçu</button>
                                                <button
                                                    onClick={() => setGcDraftView('raw')}
                                                    className={`text-xs px-2.5 py-1 rounded-r-lg border-t border-r border-b transition-colors ${gcDraftView === 'raw' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-gray-300 dark:border-white/20 text-gray-500 hover:border-fuchsia-400'}`}
                                                >Brut</button>
                                            </div>
                                            <button onClick={() => setGcDraft(null)} className="text-xs text-gray-400 hover:text-fuchsia-500 transition-colors">← Paramètres</button>
                                        </div>

                                        {/* Aperçu rendu */}
                                        {gcDraftView === 'preview' && (
                                            <div className="border border-gray-200 dark:border-white/10 rounded-xl p-5 max-h-96 overflow-y-auto bg-white dark:bg-white/[0.02]">
                                                <NotionPreview blocks={parseDraftBlocks(gcDraft.draft)} />
                                            </div>
                                        )}

                                        {/* Brouillon brut */}
                                        {gcDraftView === 'raw' && (
                                            <pre className="whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-xl p-4 max-h-96 overflow-y-auto border border-gray-200 dark:border-white/10 font-mono leading-relaxed">
                                                {gcDraft.draft}
                                            </pre>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="px-6 py-4 border-t border-gray-200 dark:border-white/10 flex justify-end gap-3">
                                {!gcGenerating && !gcDraft && (
                                    <>
                                        <ActionBtn size="md" onClick={() => { setGcModal(false); setGcDraft(null); }}>Annuler</ActionBtn>
                                        <ActionBtn size="md" variant="primary" onClick={generateCourse}>✦ Générer</ActionBtn>
                                    </>
                                )}
                                {gcDraft && !gcGenerating && (
                                    <>
                                        <ActionBtn size="md" onClick={generateCourse}>↻ Regénérer</ActionBtn>
                                        <ActionBtn size="md" variant="primary" disabled={gcValidating} onClick={validateCourse}>
                                            {gcValidating ? '⏳ Import…' : { replace: '✓ Remplacer les blocs', revise: '✓ Appliquer la révision', complete: '✓ Ajouter les compléments' }[gcMode] ?? '✓ Valider'}
                                        </ActionBtn>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {previewFc && (
                    <PreviewModal onClose={() => setPreviewFc(null)}>
                        <FlashcardSession cards={[previewFc]} user={user} onExit={() => setPreviewFc(null)} preview={true} />
                    </PreviewModal>
                )}
                {previewQz && (
                    <PreviewModal onClose={() => setPreviewQz(null)}>
                        <QuizSession quizzes={[previewQz]} user={user} onExit={() => setPreviewQz(null)} preview={true} />
                    </PreviewModal>
                )}

                <DragOverlay>
                    {activeBlock && (
                        <div className="rounded-xl border border-blue-400 ring-2 ring-blue-400 overflow-hidden shadow-xl opacity-90 bg-white dark:bg-gray-900">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800">
                                <span className="text-lg leading-none text-gray-400">⠿</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[activeBlock.type] ?? TYPE_COLORS.paragraph}`}>
                                    {TYPE_LABELS[activeBlock.type] ?? activeBlock.type}
                                </span>
                                {activeBlock.title && <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{activeBlock.title}</span>}
                                {selectedIds.has(activeBlock.id) && selectedIds.size > 1 && (
                                    <span className="ml-auto text-xs bg-blue-500 text-white rounded-full px-2 py-0.5">{selectedIds.size}</span>
                                )}
                            </div>
                        </div>
                    )}
                </DragOverlay>
            </div>
        </DndContext>
    );
}

// ─── Modales de formulaire ────────────────────────────────────────

function SubjectModal({ form: init, busy, onClose, onSave }) {
    const [f, setF] = useState(init);
    const set = k => v => setF(p => ({ ...p, [k]: v }));

    const wn = Math.max(0, Math.min(100, parseInt(f.weight_notion ?? 60) || 0));
    const we = Math.max(0, Math.min(100, parseInt(f.weight_exercice ?? 40) || 0));
    const threshold = Math.max(1, Math.min(100, parseInt(f.mastery_threshold ?? 80) || 80));
    const newPerDay = Math.max(1, Math.min(50, parseInt(f.new_items_per_day ?? 10) || 10));
    const total = wn + we;
    const defi = Math.max(0, 100 - total);

    const autoSlug = (f.name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    return (
        <Modal isOpen={true} title={f.id ? 'Modifier la matière' : 'Nouvelle matière'} onClose={onClose} onSave={() => onSave({ ...f, weight_notion: wn, weight_exercice: we, mastery_threshold: threshold, new_items_per_day: newPerDay })} busy={busy}>
            <Field label="Nom">
                <div className="flex items-center gap-3">
                    <SubjectIcon subject={f.name} className="w-8 h-8 shrink-0" />
                    <Input value={f.name} onChange={set('name')} />
                </div>
            </Field>
            <Field label="Slug URL">
                <Input value={f.slug ?? ''} onChange={set('slug')} placeholder={autoSlug || 'ex: maths'} />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Utilisé dans l'URL : <code className="bg-gray-100 dark:bg-white/10 px-1 rounded">/lecon/{f.slug || autoSlug || '…'}/…</code>
                    {!f.slug && <span className="ml-1 text-amber-500">← auto si vide</span>}
                </p>
            </Field>
            <Field label="Ordre d'affichage"><Input type="number" value={f.display_order} onChange={v => set('display_order')(+v)} /></Field>

            <Field label="Coefficients de maîtrise du chapitre">
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 w-28 shrink-0">Notions</span>
                        <input type="range" min="0" max="100" value={wn}
                            onChange={e => set('weight_notion')(+e.target.value)}
                            className="flex-1 accent-purple-500" />
                        <span className="text-xs font-bold text-purple-600 dark:text-purple-400 w-10 text-right">{wn}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 w-28 shrink-0">Exercices</span>
                        <input type="range" min="0" max="100" value={we}
                            onChange={e => set('weight_exercice')(+e.target.value)}
                            className="flex-1 accent-cyan-500" />
                        <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 w-10 text-right">{we}%</span>
                    </div>
                    <div className="flex items-center gap-3 opacity-40">
                        <span className="text-xs font-semibold text-gray-500 w-28 shrink-0">Défis (bientôt)</span>
                        <div className="flex-1 h-2 bg-gray-200 dark:bg-white/10 rounded-full" />
                        <span className="text-xs font-bold text-gray-400 w-10 text-right">{defi}%</span>
                    </div>
                    <div className={`text-xs font-semibold mt-1 ${total > 100 ? 'text-red-500' : total < 100 ? 'text-amber-500' : 'text-emerald-500'}`}>
                        Total : {total}% {total !== 100 && `— il manque ${100 - total}% (affecté aux défis)`}
                    </div>
                </div>
            </Field>

            <Field label="Seuil de déblocage du niveau suivant">
                <div className="flex items-center gap-3">
                    <input type="range" min="1" max="100" value={threshold}
                        onChange={e => set('mastery_threshold')(+e.target.value)}
                        className="flex-1 accent-emerald-500" />
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 w-10 text-right">{threshold}%</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Score de maîtrise minimal pour accéder au niveau suivant d'un chapitre.
                </p>
            </Field>

            <Field label="Nouveaux éléments SRS par jour">
                <div className="flex items-center gap-3">
                    <input type="range" min="1" max="50" value={newPerDay}
                        onChange={e => set('new_items_per_day')(+e.target.value)}
                        className="flex-1 accent-orange-500" />
                    <span className="text-xs font-bold text-orange-600 dark:text-orange-400 w-10 text-right">{newPerDay}</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Nombre maximum de flashcards/quiz jamais vus introduits par jour pour cette matière.
                </p>
            </Field>
        </Modal>
    );
}

function ChapterModal({ form: init, subjects, busy, onClose, onSave, onDelete }) {
    const [f, setF] = useState(init);
    const set = k => v => setF(p => ({ ...p, [k]: v }));
    const autoSlug = (f.name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return (
        <Modal isOpen={true} title={f.id ? 'Modifier le chapitre' : 'Nouveau chapitre'} onClose={onClose} onSave={() => onSave(f)} onDelete={onDelete} busy={busy}>
            <Field label="Matière">
                <Select value={f.subject_id} onChange={set('subject_id')} placeholder="— Choisir —"
                    options={subjects.map(s => ({ value: s.id, label: s.name }))} />
            </Field>
            <Field label="Nom du chapitre"><Input value={f.name} onChange={set('name')} placeholder="ex: Calcul littéral" /></Field>
            <Field label="Slug URL">
                <Input value={f.slug ?? ''} onChange={set('slug')} placeholder={autoSlug || 'ex: calcul-litteral'} />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    URL : <code className="bg-gray-100 dark:bg-white/10 px-1 rounded">/lecon/…/{f.slug || autoSlug || '…'}/lecon</code>
                    {!f.slug && <span className="ml-1 text-amber-500">← auto si vide</span>}
                </p>
            </Field>
            <Field label="Groupe / Thème"><Input value={f.display_group} onChange={set('display_group')} placeholder="ex: Algèbre" /></Field>
            <Field label="Année de départ (niveau 1)">
                <Select value={f.school_year} onChange={set('school_year')} placeholder="— Choisir —"
                    options={SCHOOL_YEARS.map(y => ({ value: y, label: y }))} />
            </Field>
            <Field label="Description"><Input value={f.description} onChange={set('description')} /></Field>
            <Field label="Ordre"><Input type="number" value={f.display_order} onChange={v => set('display_order')(+v)} /></Field>
        </Modal>
    );
}

function NotionModal({ form: init, chapters, busy, onClose, onSave, onDelete }) {
    const [f, setF] = useState(init);
    const set = k => v => setF(p => ({ ...p, [k]: v }));

    const chap = chapters.find(c => String(c.id) === String(f.chapter_id));
    const levelNumber = Math.max(1, Math.floor(parseFloat(f.number) || 1));
    const levelYear = chap?.school_year ? computeLevelSchoolYear(chap.school_year, levelNumber) : null;

    return (
        <Modal isOpen={true} title={f.id ? 'Modifier la notion' : 'Nouvelle notion'} onClose={onClose} onSave={() => onSave(f)} onDelete={onDelete} busy={busy}>
            <Field label="Chapitre">
                <Select value={f.chapter_id} onChange={set('chapter_id')} placeholder="— Choisir —"
                    options={chapters.map(c => ({ value: c.id, label: c.name + (c.school_year ? ` (départ ${c.school_year})` : '') }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Numéro">
                    <Input value={f.number} onChange={set('number')} placeholder="ex: 1.1, 2.3" />
                    <p className="text-xs text-gray-400 mt-1">
                        Entier = niveau · 1 décimale = séquence (1–9)
                        {levelYear && <span className="ml-1 font-medium text-cyan-600 dark:text-cyan-400">→ Niveau {levelNumber} ({levelYear})</span>}
                    </p>
                </Field>
                <Field label="Score minimum (%)"><Input type="number" value={f.min_score} onChange={v => set('min_score')(+v)} /></Field>
            </div>
            <Field label="Nom de la notion"><Input value={f.name} onChange={set('name')} placeholder="ex: Vocabulaire du calcul littéral" /></Field>
            <Field label="Description">
                <textarea value={f.description} onChange={e => set('description')(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y" />
            </Field>
        </Modal>
    );
}

function BlockModal({ form: init, busy, onClose, onSave }) {
    const [f, setF] = useState(init);
    const set = k => v => setF(p => ({ ...p, [k]: v }));
    const needsTitle = ['definition', 'theorem', 'proof', 'example', 'remark', 'note'].includes(f.type);
    return (
        <Modal isOpen={true} title={f.id ? 'Modifier le bloc' : 'Nouveau bloc'} onClose={onClose} onSave={() => onSave(f)} busy={busy}>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                    <Select value={f.type} onChange={set('type')} options={BLOCK_TYPES} />
                </Field>
                <Field label="Ordre"><Input type="number" value={f.display_order} onChange={v => set('display_order')(+v)} /></Field>
            </div>
            {needsTitle && (
                <Field label="Titre (optionnel)">
                    <Input value={f.title} onChange={set('title')} placeholder="ex: Définition du polynôme" />
                </Field>
            )}
            <MarkdownEditor label="Contenu" value={f.content} onChange={set('content')} rows={14}
                placeholder="Contenu en Markdown. Supporte LaTeX : $x^2$, **gras**, etc." />
        </Modal>
    );
}

function FlashcardModal({ form: init, busy, onClose, onSave }) {
    const [f, setF] = useState(init);
    const set = k => v => setF(p => ({ ...p, [k]: v }));
    return (
        <Modal isOpen={true} title={f.id ? 'Modifier la flashcard' : 'Nouvelle flashcard'} onClose={onClose} onSave={() => onSave(f)} busy={busy}>
            <Field label="Question">
                <textarea
                    value={f.question}
                    onChange={e => set('question')(e.target.value)}
                    rows={3}
                    placeholder="Question affichée au recto. Supporte LaTeX : $x^2$"
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y"
                />
            </Field>
            <Field label="Réponse">
                <textarea
                    value={f.answer}
                    onChange={e => set('answer')(e.target.value)}
                    rows={4}
                    placeholder="Réponse affichée au verso. Supporte LaTeX et Markdown."
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y"
                />
            </Field>
        </Modal>
    );
}

function QuizModal({ form: init, busy, onClose, onSave }) {
    const ans = init.answer || {};
    const [question, setQuestion] = useState(init.question ?? '');
    const [type, setType] = useState(ans.type ?? 'single_choice');
    const [hint, setHint] = useState(ans.hint ?? '');
    const [feedback, setFeedback] = useState(ans.feedback ?? '');

    // single_choice / multiple_choice / short_text / numeric
    const [options, setOptions] = useState(
        ans.options ?? [{ text: '', isCorrect: false, feedback: '' }]
    );
    const addOption = () => setOptions(o => [...o, { text: '', isCorrect: false, feedback: '' }]);
    const removeOption = (i) => setOptions(o => o.filter((_, idx) => idx !== i));
    const setOption = (i, key, val) => setOptions(o => o.map((opt, idx) => idx === i ? { ...opt, [key]: val } : opt));

    // matching
    const [pairs, setPairs] = useState(ans.pairs ?? [{ left: '', right: '' }]);
    const addPair = () => setPairs(p => [...p, { left: '', right: '' }]);
    const removePair = (i) => setPairs(p => p.filter((_, idx) => idx !== i));
    const setPair = (i, key, val) => setPairs(p => p.map((pair, idx) => idx === i ? { ...pair, [key]: val } : pair));

    // fill_in_the_blank
    const [fillText, setFillText] = useState(ans.text ?? '');
    const [blanks, setBlanks] = useState(ans.blanks?.length ? ans.blanks : ['']);
    const [distractors, setDistractors] = useState(ans.distractors?.length ? ans.distractors : ['']);
    const setBlank = (i, v) => setBlanks(b => b.map((x, idx) => idx === i ? v : x));
    const addBlank = () => setBlanks(b => [...b, '']);
    const removeBlank = (i) => setBlanks(b => b.filter((_, idx) => idx !== i));
    const setDistractor = (i, v) => setDistractors(d => d.map((x, idx) => idx === i ? v : x));
    const addDistractor = () => setDistractors(d => [...d, '']);
    const removeDist = (i) => setDistractors(d => d.filter((_, idx) => idx !== i));

    // ordering
    const [items, setItems] = useState(ans.items?.length ? ans.items : ['', '']);
    const setItem = (i, v) => setItems(it => it.map((x, idx) => idx === i ? v : x));
    const addItem = () => setItems(it => [...it, '']);
    const removeItem = (i) => setItems(it => it.filter((_, idx) => idx !== i));

    const isChoiceType = ['single_choice', 'multiple_choice', 'short_text', 'numeric'].includes(type);

    const buildAnswer = () => {
        const base = { type, ...(hint ? { hint } : {}), feedback };
        if (isChoiceType) return { ...base, options };
        if (type === 'matching') return { ...base, pairs };
        if (type === 'fill_in_the_blank') return { ...base, text: fillText, blanks, distractors };
        if (type === 'ordering') return { ...base, items };
        return base;
    };

    const inputClass = "w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500";

    return (
        <Modal isOpen={true} title={init.id ? 'Modifier le quiz' : 'Nouveau quiz'} onClose={onClose} onSave={() => onSave({ id: init.id, notion_id: init.notion_id, question, answer: buildAnswer() })} busy={busy} size="lg">
            {/* Type + Question */}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Type de question">
                    <Select value={type} onChange={v => setType(v)} options={QUIZ_TYPES} />
                </Field>
                <Field label="Indice (optionnel)">
                    <Input value={hint} onChange={setHint} placeholder="Aide affichée si l'élève bloque" />
                </Field>
            </div>
            <Field label="Question">
                <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={2}
                    placeholder="Texte de la question. Supporte LaTeX : $f(x) = x^2$"
                    className={`${inputClass} resize-y`} />
            </Field>

            {/* ── Choix unique / multiple / texte court / numérique ── */}
            {isChoiceType && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            {type === 'single_choice' ? 'Options (une seule correcte)' :
                                type === 'multiple_choice' ? 'Options (plusieurs correctes possibles)' :
                                    type === 'numeric' ? 'Réponses acceptées' : 'Réponses acceptées'}
                        </label>
                        <button onClick={addOption} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">+ Ajouter</button>
                    </div>
                    {options.map((opt, i) => (
                        <div key={i} className="rounded-lg border border-gray-200 dark:border-white/10 p-3 space-y-2 bg-gray-50 dark:bg-gray-800/40">
                            <div className="flex items-center gap-2">
                                <input
                                    type={type === 'multiple_choice' ? 'checkbox' : 'radio'}
                                    checked={opt.isCorrect}
                                    onChange={e => {
                                        if (type === 'single_choice' || type === 'short_text' || type === 'numeric') {
                                            setOptions(o => o.map((x, idx) => ({ ...x, isCorrect: idx === i })));
                                        } else {
                                            setOption(i, 'isCorrect', e.target.checked);
                                        }
                                    }}
                                    className="accent-cyan-500 shrink-0"
                                />
                                <input
                                    type="text"
                                    value={opt.text}
                                    onChange={e => setOption(i, 'text', e.target.value)}
                                    placeholder={type === 'numeric' ? 'Valeur acceptée (ex: 56)' : 'Texte de l\'option. Supporte LaTeX.'}
                                    className={inputClass}
                                />
                                {options.length > 1 && (
                                    <button onClick={() => removeOption(i)} className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                                )}
                            </div>
                            <input
                                type="text"
                                value={opt.feedback}
                                onChange={e => setOption(i, 'feedback', e.target.value)}
                                placeholder="Feedback affiché après la réponse (optionnel)"
                                className={inputClass}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* ── Association ── */}
            {type === 'matching' && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Paires à associer</label>
                        <button onClick={addPair} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">+ Ajouter</button>
                    </div>
                    {pairs.map((pair, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input type="text" value={pair.left} onChange={e => setPair(i, 'left', e.target.value)}
                                placeholder="Élément gauche" className={inputClass} />
                            <span className="text-gray-400 shrink-0">↔</span>
                            <input type="text" value={pair.right} onChange={e => setPair(i, 'right', e.target.value)}
                                placeholder="Élément droit" className={inputClass} />
                            {pairs.length > 2 && (
                                <button onClick={() => removePair(i)} className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ── Texte à trous ── */}
            {type === 'fill_in_the_blank' && (
                <div className="space-y-3">
                    <Field label="Texte avec trous (utilise [_] pour chaque trou)">
                        <textarea value={fillText} onChange={e => setFillText(e.target.value)} rows={3}
                            placeholder="Le théorème de [_] s'applique aux triangles [_]."
                            className={`${inputClass} resize-y`} />
                        <p className="text-xs text-gray-400 mt-1">Nombre de [_] = nombre de bonnes réponses ci-dessous (dans l'ordre).</p>
                    </Field>
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bonnes réponses (dans l'ordre)</label>
                            <button onClick={addBlank} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">+ Ajouter</button>
                        </div>
                        {blanks.map((b, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 w-5 shrink-0 text-right">{i + 1}.</span>
                                <input type="text" value={b} onChange={e => setBlank(i, e.target.value)}
                                    placeholder={`Trou ${i + 1}`} className={inputClass} />
                                {blanks.length > 1 && (
                                    <button onClick={() => removeBlank(i)} className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Distracteurs (fausses options)</label>
                            <button onClick={addDistractor} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">+ Ajouter</button>
                        </div>
                        {distractors.map((d, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <input type="text" value={d} onChange={e => setDistractor(i, e.target.value)}
                                    placeholder="Option incorrecte" className={inputClass} />
                                {distractors.length > 1 && (
                                    <button onClick={() => removeDist(i)} className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Mise en ordre ── */}
            {type === 'ordering' && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Éléments dans l'ordre correct</label>
                        <button onClick={addItem} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">+ Ajouter</button>
                    </div>
                    {items.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-5 shrink-0 text-right">{i + 1}.</span>
                            <input type="text" value={item} onChange={e => setItem(i, e.target.value)}
                                placeholder={`Élément ${i + 1}`} className={inputClass} />
                            {items.length > 2 && (
                                <button onClick={() => removeItem(i)} className="shrink-0 text-red-400 hover:text-red-600 text-sm leading-none">✕</button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Feedback global */}
            <Field label="Feedback global (affiché après validation)">
                <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={2}
                    placeholder="Explication ou rappel de cours après la réponse."
                    className={`${inputClass} resize-y`} />
            </Field>
        </Modal>
    );
}
